/**
 * StateManagerService - GameEngine's persistence and coordination helper
 * Implements IStateManager interface to assist GameEngine with persistence
 * Components primarily interact with GameEngine, which coordinates with this service
 */

import { GameState, GameMode, UserSettings, DailyState, PersistentState } from '../types/GameTypes';
import { GameEngine, IStateManager } from './GameEngine';
import { ScoreCalculator } from './ScoreCalculator';
import { WordSelector } from './WordSelector';
import { AnalyticsService } from './AnalyticsService';
import { GameAnalyticsIntegration } from './GameAnalyticsIntegration';
import { SharingPlatform, ModalType } from '../types/AnalyticsConstants';
import { GAME_CONFIG } from '../config';

// Repository imports
import { StorageService } from './StorageService';
import { WordRepository } from '../repositories/WordRepository';
import { GameStateRepository } from '../repositories/GameStateRepository';
import { DailyStateRepository } from '../repositories/DailyStateRepository';
import { PersistentStateRepository } from '../repositories/PersistentStateRepository';
import { SettingsRepository } from '../repositories/SettingsRepository';

/**
 * State management service that GameEngine uses for persistence and coordination
 * Provides clean interface while maintaining separation of concerns
 */
export class StateManagerService implements IStateManager {
  private storageService: StorageService;
  private wordRepository: WordRepository;
  private gameStateRepository: GameStateRepository;
  private dailyStateRepository: DailyStateRepository;
  private persistentStateRepository: PersistentStateRepository;
  private settingsRepository: SettingsRepository;
  private wordSelector: WordSelector;
  private gameAnalytics: GameAnalyticsIntegration;

  constructor(analyticsService?: AnalyticsService) {
    // Initialize storage service
    this.storageService = new StorageService();
    
    // Initialize repositories
    this.wordRepository = new WordRepository();
    this.gameStateRepository = new GameStateRepository(this.storageService);
    this.dailyStateRepository = new DailyStateRepository(this.storageService);
    this.persistentStateRepository = new PersistentStateRepository(this.storageService);
    this.settingsRepository = new SettingsRepository(this.storageService);
    
    // Initialize services
    this.wordSelector = new WordSelector(this.wordRepository);
    this.gameAnalytics = new GameAnalyticsIntegration(analyticsService);
  }

  /**
   * Start a new game - Components call this, it coordinates with GameEngine
   */
  startNewGame(gameMode: GameMode, wordLength?: number, trackAnalytics: boolean = true): GameState {
    // Load user settings (without migration since that should be done at startup)
    const settings = this.settingsRepository.loadUserSettings();
    
    // Select word based on game mode
    let selectedWord: string;
    
    if (gameMode === 'daily') {
      // DAILY MODE: Unchanged - continues using WORD_LISTS
      const dailyState = this.dailyStateRepository.loadDailyState();
      const currentLength = dailyState?.currentWordLength || 5;
      const wordSelection = this.wordSelector.selectDailyWord(currentLength);
      selectedWord = wordSelection.word;
    } else {
      // FREE PLAY MODE: Use word packs from settings
      try {
        // Word pack determines the word length automatically
        const wordSelection = this.wordSelector.selectFreePlayWordFromPack(settings.selectedWordPack);
        selectedWord = wordSelection.word;
      } catch (error) {
        console.error('Error selecting word from pack, falling back to length-based selection:', error);
        // Fallback to length-based selection if word pack fails
        // Use the passed wordLength parameter, or default to game default if not specified
        const fallbackLength = wordLength || GAME_CONFIG.defaultWordLength;
        const wordSelection = this.wordSelector.selectFreePlayWord(fallbackLength);
        selectedWord = wordSelection.word;
      }
    }

    // Daily mode always uses RSTLNE mode, free-play mode respects user preference
    const shouldUseRstlneMode = gameMode === 'daily' ? true : settings.rstlneMode;
    
    // Let GameEngine handle the game logic and initial reveals
    const gameState = GameEngine.startNewGame(selectedWord, shouldUseRstlneMode, this, gameMode);
    
    // Track analytics for game start (only if requested)
    if (trackAnalytics) {
      this.gameAnalytics.trackGameStart(gameMode, selectedWord.length, selectedWord);
      
      // Track daily challenge start if this is the first word of a new daily challenge
      if (gameMode === 'daily') {
        const dailyState = this.dailyStateRepository.loadDailyState();
        this.gameAnalytics.trackDailyChallengeStart(dailyState);
      }
    }
    
    return gameState;
  }

  /**
   * Make a letter guess - Components call this, it coordinates with GameEngine
   */
  makeLetterGuess(currentState: GameState, letter: string, gameMode: GameMode): GameState {
    const previousGameOver = currentState.gameOver;
    const newState = GameEngine.makeLetterGuess(currentState, letter, this, gameMode);
    
    // Track game completion for free-play mode (daily mode is handled by handleDailyGameCompletion)
    if (!previousGameOver && newState.gameOver && gameMode === 'free-play') {
      this.gameAnalytics.trackGameComplete(newState, gameMode);
    }
    
    return newState;
  }

  /**
   * Save game state (called by GameEngine)
   */
  saveGameState(gameState: GameState, gameMode: GameMode): void {
    if (gameMode === 'daily') {
      this.gameStateRepository.saveCurrentGameState(gameState);
    }
  }

  /**
   * Handle daily game completion (called by GameEngine for daily mode only)
   */
  handleDailyGameCompletion(gameState: GameState): void {
    const dailyState = this.dailyStateRepository.loadDailyState();
    if (!dailyState) return;

    // Calculate score
    const score = ScoreCalculator.calculateGameScore(gameState);
    
    // Track game completion
    this.gameAnalytics.trackGameComplete(gameState, 'daily');
    
    // Add word result
    const wordResult = {
      word: gameState.secretWord,
      length: gameState.secretWord.length,
      revealed: Array(gameState.secretWord.length).fill(true),
      score,
      letterGuesses: [...gameState.guessedLetters],
      success: gameState.gameWon
    };

    // Update daily state
    const updatedDailyState = {
      ...dailyState,
      results: [...dailyState.results, wordResult],
      totalScore: dailyState.totalScore + score
    };

    // Check if daily challenge is complete
    if (this.wordSelector.isDailyProgressionComplete(dailyState.currentWordLength)) {
      updatedDailyState.completed = true;
      
      // Track daily challenge completion
      this.gameAnalytics.trackDailyChallengeComplete(updatedDailyState.totalScore);
      
      // Update persistent state for streak
      const persistentState = this.persistentStateRepository.loadPersistentState();
      const updatedPersistentState = this.persistentStateRepository.updatePersistentStateForCompletedChallenge(
        persistentState,
        updatedDailyState.totalScore
      );
      
      // Track streak milestones
      if (updatedPersistentState.streak > persistentState.streak) {
        this.gameAnalytics.trackStreakMilestone(updatedPersistentState.streak);
      }
    } else {
      // Move to next word length
      const nextLength = this.wordSelector.getNextDailyLength(dailyState.currentWordLength);
      if (nextLength) {
        updatedDailyState.currentWordLength = nextLength;
      }
    }

    this.dailyStateRepository.saveDailyState(updatedDailyState);
  }

  /**
   * Load existing game state (for daily mode restoration)
   */
  loadGameState(gameMode: GameMode): GameState | null {
    if (gameMode === 'daily') {
      return this.gameStateRepository.loadCurrentGameState();
    }
    return null;
  }

  /**
   * Load user settings
   */
  loadSettings(): UserSettings {
    return this.settingsRepository.loadUserSettings();
  }

  /**
   * Save user settings
   */
  saveSettings(settings: Partial<UserSettings>): void {
    // Load current settings and merge with new ones
    const currentSettings = this.settingsRepository.loadUserSettings();
    const updatedSettings = { ...currentSettings, ...settings };
    
    // Track settings changes
    for (const [key, newValue] of Object.entries(settings)) {
      const oldValue = currentSettings[key as keyof UserSettings];
      if (oldValue !== newValue) {
        this.gameAnalytics.trackSettingsChange(
          key,
          String(oldValue),
          String(newValue)
        );
      }
    }
    
    this.settingsRepository.saveUserSettings(updatedSettings);
  }

  /**
   * Load daily state
   */
  loadDailyState(): DailyState | null {
    return this.dailyStateRepository.loadDailyState();
  }

  /**
   * Create new daily state
   */
  createNewDailyState(): DailyState {
    return this.dailyStateRepository.createNewDailyState();
  }

  /**
   * Load persistent state
   */
  loadPersistentState(): PersistentState {
    return this.persistentStateRepository.loadPersistentState();
  }

  /**
   * Check if daily state is from today
   */
  isDailyStateFromToday(state: DailyState | null): boolean {
    return this.dailyStateRepository.isDailyStateFromToday(state);
  }

  /**
   * Check if storage is available
   */
  isStorageAvailable(): boolean {
    return this.storageService.isAvailable();
  }

  /**
   * Track mode switching
   */
  trackModeSwitch(fromMode: GameMode, toMode: GameMode, triggerSource: 'button' | 'navigation'): void {
    this.gameAnalytics.trackModeSwitch(fromMode, toMode, triggerSource);
  }

  /**
   * Track errors encountered
   */
  trackError(errorType: string, errorContext: string, userActionTaken?: string): void {
    this.gameAnalytics.trackError(errorType, errorContext, userActionTaken);
  }

  /**
   * Track performance issues
   */
  trackPerformanceIssue(metricName: 'LCP' | 'FID' | 'CLS' | 'custom', value: number, thresholdExceeded: boolean): void {
    this.gameAnalytics.trackPerformanceIssue(metricName, value, thresholdExceeded);
  }

  /**
   * Track user engagement metrics
   */
  trackUserEngagement(sessionDuration: number, gamesPerSession: number, returnVisitor: boolean): void {
    this.gameAnalytics.trackUserEngagement(sessionDuration, gamesPerSession, returnVisitor);
  }

  /**
   * Track sharing events - important for NPS measurement
   */
  trackShare(platform: SharingPlatform, dailyScore: number, streakLength: number, wasSuccessful: boolean = true): void {
    this.gameAnalytics.trackShare(platform, dailyScore, streakLength, wasSuccessful);
  }

  /**
   * Track modal opening events - helps understand user engagement
   */
  trackModalOpen(modalType: ModalType): void {
    this.gameAnalytics.trackModalOpen(modalType);
  }

  /**
   * Get word selector for additional word operations
   */
  getWordSelector(): WordSelector {
    return this.wordSelector;
  }

}
